import * as React from 'react';
import { BaSeI18nContext } from '../../../contexts/i18n';
import { BaSeColors } from '../../../theme/colors';
import { transformLightness } from '../../../utils/color-transformation/transform-lightness';
import { objectNotEmpty } from '../../../utils/object-utils';
import {
  BaSeTooltip,
  EmbedTooltipProps,
  TooltipProps,
} from '../../alerts/tooltip/tooltip';
import { BaSeBadge } from '../../badge/badge';
import { BaSeIconButton } from '../../button/shape-button/icon-button';
import { BaSeIcon } from '../../image/icon';
import { BaSeSmall1 } from '../../typography/small/small1';
import { BaSeSmall2 } from '../../typography/small/small2';
import { BaSeTheme } from './../../../theme';
import { mapTagValues } from './map-tag-style';
import {
  StyledTagTextProps,
  TagClose,
  TagContainerStyled,
  TagIcon,
  TagStyledProps,
  TagText,
} from './tag-styled';

export interface OverrideTagColorProps {
  iconColor?: string;
  textColor?: string;
  borderColor?: string;
  closeButtonColor?: string;
  selectedIconColor?: string;
  selectedTextColor?: string;
  selectedBorderColor?: string;
  selectedCloseButtonColor?: string;
}

export interface TagProps
  extends Partial<Omit<TagStyledProps, 'bigSize' | 'mediumSize' | 'smallSize'>>,
    Pick<StyledTagTextProps, 'limitedWidth'> {
  label: string;
  color?: string;
  overrideColor?: OverrideTagColorProps;
  tooltip?: EmbedTooltipProps;
}

export type ContainerProps = React.HTMLAttributes<HTMLDivElement> &
  TagStyledProps;

export const BaSeTag: React.FC<TagProps> = ({
  icon,
  label,
  overrideColor,
  limitedWidth,
  size = 'medium',
  type = 'primary',
  color = 'default',
  themeMode = 'light',
  isSelected = false,
  isDisabled = false,
  tooltip = {},
  onClick: clickCallback,
  onClose: closeCallback,
}) => {
  if (themeMode && !['light', 'dark'].includes(themeMode)) {
    throw Error('Atributo "themeMode" deve ser do tipo: "light" ou "dark"');
  }

  if (type && !['primary', 'secondary'].includes(type)) {
    throw Error('Atributo "type" deve ser do tipo: "primary" ou "secondary"');
  }

  const { getMessage } = React.useContext(BaSeI18nContext);

  const iconProps = React.useMemo(
    () => ({
      name: 'check',
      description: getMessage('tag.selected'),
      size: 12,
    }),
    [getMessage],
  );

  const { onClick, onClose } = React.useMemo(
    () => ({
      onClick: isDisabled ? undefined : clickCallback,
      onClose: isDisabled ? undefined : closeCallback,
    }),
    [clickCallback, closeCallback, isDisabled],
  );

  const containerProps: ContainerProps = React.useMemo(() => {
    const props: ContainerProps = {
      bigSize: 30,
      mediumSize: 24,
      smallSize: 18,
      className: 'BaSe--tag',
      icon,
      size,
      type,
      isSelected,
      isDisabled,
      themeMode,
      tabIndex: -1,
      onClose,
    };

    if (onClick) {
      props.role = 'button';
      props.tabIndex = 0;
      props.onKeyUp = (e) => e.key === 'Enter' && onClick(e);
      props.onClick = onClick;
    }

    return props;
  }, [icon, size, type, isSelected, isDisabled, themeMode, onClose, onClick]);

  const getColorsAtributes = React.useCallback(
    (newColor: string) => {
      if (newColor) {
        return mapTagValues(newColor)[type][themeMode];
      }
      return mapTagValues('default')[type][themeMode];
    },
    [themeMode, type],
  );

  const getValues = React.useCallback(() => {
    if (isDisabled) {
      return {
        bgC: BaSeTheme.colors.institucionais.cinzaSebrae90,
        bcCHover: BaSeTheme.colors.institucionais.cinzaSebrae75,
        boxShadowFocus: BaSeTheme.colors.institucionais.cinzaSebrae75,
        border: isSelected
          ? BaSeTheme.colors.institucionais.cinzaSebrae75
          : BaSeTheme.colors.institucionais.cinzaSebrae90,
        newIconColor: BaSeTheme.colors.institucionais.cinzaSebrae75,
      };
    }

    const colorValue = getColorsAtributes(color);
    if (isSelected && type === 'secondary') {
      return {
        bgC: colorValue.iconColor,
        bcCHover: colorValue.style.bcCHover,
        boxShadowFocus: colorValue.style.boxShadowFocus,
        border: colorValue.style.border,
        newIconColor: colorValue.selectedForeground ?? colorValue.style.bgC,
      };
    }
    if (isSelected && type === 'primary') {
      return {
        bgC: themeMode === 'dark' ? colorValue.iconColor : colorValue.style.bgC,
        bcCHover: colorValue.style.bcCHover,
        boxShadowFocus: colorValue.style.boxShadowFocus,
        border: colorValue.iconColor,
        newIconColor:
          themeMode === 'dark' ? colorValue.style.bgC : colorValue.iconColor,
      };
    }
    return {
      bgC: colorValue.style.bgC,
      bcCHover: colorValue.style.bcCHover,
      boxShadowFocus: colorValue.style.boxShadowFocus,
      border: colorValue.style.border,
      newIconColor: colorValue.iconColor,
    };
  }, [color, getColorsAtributes, isDisabled, isSelected, themeMode, type]);

  const { bgC, bcCHover, boxShadowFocus, border, newIconColor } = React.useMemo(
    () => getValues(),
    [getValues],
  );

  const resolvedDefaultColors = React.useMemo(
    () => ({
      icon: overrideColor?.iconColor ?? newIconColor,
      text: overrideColor?.textColor ?? newIconColor,
      border: overrideColor?.borderColor ?? border,
      closeButton: overrideColor?.closeButtonColor ?? newIconColor,
    }),
    [
      border,
      newIconColor,
      overrideColor?.borderColor,
      overrideColor?.closeButtonColor,
      overrideColor?.iconColor,
      overrideColor?.textColor,
    ],
  );

  const resolvedSelectedColors = React.useMemo(
    () => ({
      icon: overrideColor?.selectedIconColor ?? newIconColor,
      text: overrideColor?.selectedTextColor ?? newIconColor,
      border: overrideColor?.selectedBorderColor ?? border,
      closeButton: overrideColor?.selectedCloseButtonColor ?? newIconColor,
    }),
    [
      border,
      newIconColor,
      overrideColor?.selectedBorderColor,
      overrideColor?.selectedCloseButtonColor,
      overrideColor?.selectedIconColor,
      overrideColor?.selectedTextColor,
    ],
  );

  const resolvedColors = React.useMemo(
    () => (isSelected ? resolvedSelectedColors : resolvedDefaultColors),
    [isSelected, resolvedDefaultColors, resolvedSelectedColors],
  );

  const hoverColors = React.useMemo(
    () => ({
      icon: getColorsAtributes(overrideColor?.iconColor ?? color).style
        .bcCHover,
      text: getColorsAtributes(overrideColor?.textColor ?? color).style
        .bcCHover,
      border: getColorsAtributes(overrideColor?.borderColor ?? color).style
        .bcCHover,
      closeButton: getColorsAtributes(overrideColor?.closeButtonColor ?? color)
        .style.bcCHover,
    }),
    [
      color,
      getColorsAtributes,
      overrideColor?.borderColor,
      overrideColor?.closeButtonColor,
      overrideColor?.iconColor,
      overrideColor?.textColor,
    ],
  );

  const [isHover, setIsHover] = React.useState<boolean>(false);
  const [isHoverClose, setIsHoverClose] = React.useState<boolean>(false);
  const [isFocus, setIsFocus] = React.useState<boolean>(false);

  const divRef = React.useRef<HTMLDivElement>(null);
  const [isTooltipOpen, setIsTooltipOpen] = React.useState(false);
  const Text = size === 'small' ? BaSeSmall2 : BaSeSmall1;

  const defineIconColor = React.useMemo(() => {
    if (isSelected) {
      return type === 'primary'
        ? transformLightness(resolvedColors.icon, 0.1)
        : resolvedColors.icon;
    }

    if (isHover || isFocus) {
      return hoverColors.icon;
    }

    return resolvedColors.icon;
  }, [
    isSelected,
    isHover,
    isFocus,
    resolvedColors.icon,
    type,
    hoverColors.icon,
  ]);

  const defineTextColor = React.useMemo(() => {
    if (isSelected) {
      return type === 'primary'
        ? transformLightness(resolvedColors.text, 0.1)
        : resolvedColors.text;
    }

    if (isHover || isFocus) {
      return hoverColors.text;
    }

    return resolvedColors.text;
  }, [
    isSelected,
    isHover,
    isFocus,
    resolvedColors.text,
    type,
    hoverColors.text,
  ]);

  const resolvedBorderColor = React.useMemo(() => {
    if ((isHover || isFocus) && onClick && type === 'secondary') {
      return hoverColors.border;
    }

    return resolvedColors.border;
  }, [
    isHover,
    isFocus,
    onClick,
    type,
    hoverColors.border,
    resolvedColors.border,
  ]);

  const resolvedCloseButtonColor = React.useMemo(
    () =>
      themeMode === 'dark' && isHoverClose ? bgC : resolvedColors.closeButton,
    [themeMode, isHoverClose, bgC, resolvedColors.closeButton],
  );

  const buildTag = React.useCallback(
    () => (
      <TagContainerStyled
        {...containerProps}
        bgC={bgC}
        bcCHover={bcCHover}
        boxShadowFocus={boxShadowFocus}
        border={resolvedBorderColor}
        newIconColor={newIconColor}
        onMouseEnter={() => {
          if (onClick) {
            setIsHover(true);
          }
          setIsTooltipOpen(true);
        }}
        onMouseLeave={() => {
          if (onClick) {
            setIsHover(false);
          }
          setIsTooltipOpen(false);
        }}
        onFocus={() => onClick && setIsFocus(true)}
        onBlur={() => onClick && setIsFocus(false)}
        ref={divRef}
        aria-checked={isSelected}
        tabIndex={isDisabled ? -1 : containerProps.tabIndex}
      >
        {icon && (
          <TagIcon
            className="BaSe--tag-icon icon"
            type={type}
            newIconColor={
              isSelected
                ? resolvedColors.icon
                : isHover && !isSelected
                  ? hoverColors.icon
                  : resolvedColors.icon
            }
            size={size}
          >
            <BaSeIcon
              size={size === 'big' ? 26 : size === 'medium' ? 18 : 14}
              name={icon}
              description={getMessage('icon.label', label)}
              color={defineIconColor}
            />
          </TagIcon>
        )}
        {label && (
          <TagText
            className="BaSe--tag-text text"
            newIconColor={newIconColor}
            size={size}
            icon={icon}
            limitedWidth={limitedWidth}
            onClose={onClose}
          >
            <Text color={defineTextColor}>{label}</Text>
          </TagText>
        )}
        {onClose && (
          <TagClose
            className="BaSe--tag-close close"
            onClose={onClose}
            bcCHover={resolvedCloseButtonColor}
            size={size}
            type={type}
            themeMode={themeMode}
            onMouseEnter={() => {
              setIsHoverClose(true);
            }}
            onMouseLeave={() => {
              setIsHoverClose(false);
            }}
          >
            <BaSeIconButton
              nameIcon="close-big"
              descriptionIcon={getMessage('buttonIcon.iconDescription.close')}
              buttonType="button"
              shape="circle"
              size={size}
              sizeIcon={size}
              type="tertiary"
              color={resolvedCloseButtonColor}
              onClick={(e) => {
                if (onClick) {
                  e.stopPropagation();
                }
                onClose(e);
              }}
            />
          </TagClose>
        )}
        {objectNotEmpty(tooltip) && (
          <BaSeTooltip
            buddyRef={divRef}
            open={isTooltipOpen}
            direction="top"
            {...(tooltip as Omit<TooltipProps, 'open' | 'buddyRef'>)}
          />
        )}
      </TagContainerStyled>
    ),
    [
      Text,
      bcCHover,
      bgC,
      boxShadowFocus,
      containerProps,
      defineIconColor,
      defineTextColor,
      getMessage,
      hoverColors.icon,
      icon,
      isDisabled,
      isHover,
      isSelected,
      isTooltipOpen,
      label,
      limitedWidth,
      newIconColor,
      onClick,
      onClose,
      resolvedBorderColor,
      resolvedCloseButtonColor,
      resolvedColors.icon,
      size,
      themeMode,
      tooltip,
      type,
    ],
  );

  return isSelected ? (
    <BaSeBadge
      height={20}
      width={20}
      color={isDisabled ? BaSeColors.institucionais.cinzaSebrae60 : color}
      type={type}
      position={{ right: 12, bottom: 10 }}
      value={iconProps}
    >
      {buildTag()}
    </BaSeBadge>
  ) : (
    buildTag()
  );
};

BaSeTag.displayName = 'BaSeTag';
